<template>
  <div ref="builderRef" class="custom-fields-builder">
    <button v-if="!fields.length" type="button" class="cf-empty" data-testid="add-custom-field" @click="addField">
      <span class="cf-empty-icon">
        <Icon name="general.plus" size="sm" />
      </span>
      <span class="cf-empty-text">
        <span class="cf-empty-title">Add custom fields</span>
        <span class="cf-empty-description">Ask people for details like an order number or email when they reply.</span>
      </span>
    </button>

    <div v-else class="cf-header">
      <span class="form-label">
        Custom fields
        <span class="cf-count" data-testid="custom-field-count">{{ fields.length }} of {{ MAX_CUSTOM_FIELDS }}</span>
      </span>
      <Button
        v-if="fields.length < MAX_CUSTOM_FIELDS"
        type="link"
        variant="blue"
        text="Add field"
        icon="general.plus"
        data-testid="add-custom-field"
        @click="addField"
      />
      <span v-else class="cf-limit">Limit reached</span>
    </div>

    <FormKit type="list" name="customFields">
      <div v-if="fields.length" class="cf-list">
        <FormKit v-for="(field, index) in fields" :key="rowIds[index]" type="group">
          <div class="cf-row" :class="{ 'cf-row-open': isOpen(index) }" data-testid="custom-field-row">
            <button v-show="!isOpen(index)" type="button" class="cf-summary" data-testid="custom-field-summary" @click="openRow(index)">
              <Icon name="general.chevronRight" size="sm" class="cf-chevron" />
              <span class="cf-summary-label" :class="{ 'cf-summary-label-empty': !field.label.custom.trim() }">
                {{ field.label.custom.trim() || 'Untitled field' }}<span v-if="!field.optional" class="cf-required-mark">*</span>
              </span>
              <Icon v-if="isIncomplete(field)" name="general.exclamationCircleSolid" size="sm" class="cf-warning" data-testid="custom-field-incomplete" />
              <span class="cf-type-badge">{{ typeLabel(field.type) }}</span>
            </button>

            <div v-show="isOpen(index)" class="cf-editor">
              <button type="button" class="cf-icon-button" title="Close field" data-testid="custom-field-close" @click="closeRow(index)">
                <Icon name="general.chevronDown" size="sm" />
              </button>

              <div class="cf-editor-content">
                <div class="cf-editor-main">
                  <FormKit
                    type="text"
                    name="label"
                    placeholder="Field label"
                    :model-value="field.label.custom"
                    maxlength="200"
                    validation="required:trim"
                    validation-visibility="blur"
                    :validation-messages="{ required: 'Add a label for this field.' }"
                    :sections-schema="errorSections"
                    data-testid="custom-field-label"
                    outer-class="!mb-0 flex-1 min-w-[10rem]"
                    @input="(value: string) => updateLabel(index, value)"
                  />

                  <FormKit
                    type="select"
                    name="type"
                    :options="fieldTypeOptions"
                    :model-value="field.type"
                    data-testid="custom-field-type"
                    outer-class="!mb-0 w-36"
                    input-class="dark:!bg-zinc-800 dark:!text-zinc-200"
                    @input="(type: CustomFieldType) => updateType(index, type)"
                  />
                </div>

                <div v-if="field.type === 'dropdown'" class="cf-options">
                  <span
                    v-for="(option, optionIndex) in field.dropdown?.options || []"
                    :key="`${optionIndex}-${option.label}`"
                    class="cf-chip"
                    data-testid="custom-field-option"
                  >
                    {{ option.label }}
                    <button
                      type="button"
                      class="cf-chip-remove"
                      title="Remove option"
                      data-testid="custom-field-option-remove"
                      @click="removeOption(index, optionIndex)"
                    >
                      <Icon name="general.close" size="xs" />
                    </button>
                  </span>
                  <input
                    v-if="(field.dropdown?.options.length || 0) < MAX_DROPDOWN_OPTIONS"
                    type="text"
                    class="cf-option-input"
                    placeholder="Add option, press Enter"
                    maxlength="200"
                    data-testid="custom-field-option-input"
                    @keydown.enter.prevent="addOption(index, $event)"
                    @blur="addOption(index, $event)"
                  />
                  <p v-if="!field.dropdown?.options.length" class="cf-hint">Add at least one option.</p>
                  <p v-if="(field.dropdown?.options.length || 0) >= MAX_DROPDOWN_OPTIONS" class="cf-hint" data-testid="custom-field-option-limit">
                    You can add up to {{ MAX_DROPDOWN_OPTIONS }} options.
                  </p>
                </div>

                <div class="cf-editor-footer">
                  <FormKit
                    type="toggle"
                    name="required"
                    label="Required"
                    :model-value="!field.optional"
                    data-testid="custom-field-required"
                    outer-class="!mb-0"
                    wrapper-class="!inline-flex"
                    @input="(required: boolean) => updateRequired(index, required)"
                  />
                  <Button type="link" variant="red" text="Remove" icon="general.trash" data-testid="custom-field-remove" @click="removeField(index)" />
                </div>
              </div>
            </div>
          </div>
        </FormKit>
      </div>
    </FormKit>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import { FormKit } from '@formkit/vue';
import { Button, Icon } from '@/storybook';
import type { CustomField, CustomFieldType } from '../../types';
import { isCustomFieldIncomplete as isIncomplete } from '../../utils/customFields';

// Matches RequestContent::MAX_CUSTOM_FIELDS on the backend.
const MAX_CUSTOM_FIELDS = 10;
// Matches RequestContent::MAX_DROPDOWN_OPTIONS on the backend.
const MAX_DROPDOWN_OPTIONS = 50;

const fieldTypeOptions: Array<{ label: string; value: CustomFieldType }> = [
  { label: 'Short text', value: 'text' },
  { label: 'Number', value: 'numeric' },
  { label: 'Email', value: 'email' },
  { label: 'Phone', value: 'phone' },
  { label: 'Dropdown', value: 'dropdown' },
  { label: 'Checkbox', value: 'checkbox' },
];

interface Props {
  modelValue?: CustomField[];
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
});

const emit = defineEmits<{ 'update:modelValue': [value: CustomField[]] }>();

const cloneFields = (value: CustomField[]): CustomField[] => {
  return JSON.parse(JSON.stringify(value || []));
};

const fields = ref<CustomField[]>(cloneFields(props.modelValue));
const builderRef = ref<HTMLElement | null>(null);

// Rows need a stable identity for v-for keys and open state that survives removing an earlier row.
let nextRowId = 0;
const newRowIds = (count: number) => Array.from({ length: count }, () => nextRowId++);
const rowIds = ref<number[]>(newRowIds(fields.value.length));
const openRowIds = ref(new Set<number>());

const isOpen = (index: number) => openRowIds.value.has(rowIds.value[index]);

const openRow = (index: number) => {
  openRowIds.value.add(rowIds.value[index]);
};

const closeRow = (index: number) => {
  openRowIds.value.delete(rowIds.value[index]);
};

const typeLabel = (type: CustomFieldType) => fieldTypeOptions.find(option => option.value === type)?.label || type;

// Saved keys and option values are what existing replies are matched against, so they never change.
const savedKeys = new Set(fields.value.map(field => field.key).filter(Boolean));
const savedOptionValues = new Set(fields.value.flatMap(field => (field.dropdown?.options || []).map(option => `${field.key}:${option.value}`)));

let lastEmitted = '';

watch(
  () => props.modelValue,
  newValue => {
    const incoming = JSON.stringify(newValue || []);
    if (incoming !== lastEmitted && incoming !== JSON.stringify(fields.value)) {
      fields.value = cloneFields(newValue || []);
      rowIds.value = newRowIds(fields.value.length);
      openRowIds.value = new Set();
    }
  }
);

const slugify = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

const uniqueSlug = (base: string, takenKeys: string[]): string => {
  let candidate = base;
  let suffix = 2;
  while (takenKeys.includes(candidate)) {
    candidate = `${base}_${suffix}`;
    suffix += 1;
  }
  return candidate;
};

const errorSections = { message: { attrs: { 'data-testid': 'custom-field-error' } } };

// The server stores the ticket ID under custom_id, so fields cannot use it.
const RESERVED_KEYS = ['custom_id'];

const buildOptions = (field: CustomField, key: string) => {
  const takenValues: string[] = [];
  return (field.dropdown?.options || [])
    .filter(option => option.label.trim())
    .map((option, optionIndex) => {
      const value = savedOptionValues.has(`${key}:${option.value}`)
        ? option.value
        : uniqueSlug(slugify(option.label) || `option_${optionIndex + 1}`, takenValues);
      takenValues.push(value);
      return { label: option.label, value };
    });
};

const emitFields = () => {
  const takenKeys: string[] = [...RESERVED_KEYS, ...savedKeys];
  const withKeys = fields.value.map((field, index) => {
    const key = savedKeys.has(field.key) ? field.key : uniqueSlug(slugify(field.label.custom) || `field_${index + 1}`, takenKeys);
    takenKeys.push(key);
    if (!field.dropdown) {
      return { ...field, key };
    }
    return { ...field, key, dropdown: { ...field.dropdown, options: buildOptions(field, key) } };
  });
  lastEmitted = JSON.stringify(withKeys);
  emit('update:modelValue', withKeys);
};

const addField = () => {
  fields.value.push({
    key: '',
    type: 'text',
    label: { type: 'custom', custom: '' },
    optional: true,
  });
  rowIds.value.push(nextRowId++);
  openRow(fields.value.length - 1);
  emitFields();
  nextTick(() => {
    const labels = builderRef.value?.querySelectorAll<HTMLInputElement>('input[data-testid="custom-field-label"]');
    labels?.[labels.length - 1]?.focus();
  });
};

const removeField = (index: number) => {
  closeRow(index);
  fields.value.splice(index, 1);
  rowIds.value.splice(index, 1);
  emitFields();
};

const updateLabel = (index: number, value: string) => {
  fields.value[index].label.custom = value;
  emitFields();
};

const updateRequired = (index: number, required: boolean) => {
  fields.value[index].optional = !required;
  emitFields();
};

const updateType = (index: number, type: CustomFieldType) => {
  const field = fields.value[index];
  field.type = type;
  if (type === 'dropdown' && !field.dropdown) {
    field.dropdown = { defaultValue: null, options: [] };
  }
  emitFields();
};

const addOption = (index: number, event: Event) => {
  const input = event.target as HTMLInputElement;
  const label = input.value.trim();
  if (!label) {
    return;
  }
  const field = fields.value[index];
  if (!field.dropdown) {
    field.dropdown = { defaultValue: null, options: [] };
  }
  input.value = '';
  if (field.dropdown.options.some(option => option.label.toLowerCase() === label.toLowerCase())) {
    return;
  }
  field.dropdown.options.push({ label, value: '' });
  emitFields();
};

const removeOption = (index: number, optionIndex: number) => {
  const field = fields.value[index];
  if (!field.dropdown) {
    return;
  }
  field.dropdown.options.splice(optionIndex, 1);
  emitFields();
};
</script>

<style scoped>
.custom-fields-builder {
  @apply flex flex-col gap-2 mb-5;
}

.cf-header {
  @apply flex items-center justify-between;
}

.form-label {
  @apply flex items-center gap-1.5 text-sm font-medium text-zinc-700 dark:text-zinc-300;
}

.cf-count,
.cf-limit {
  @apply text-xs font-normal text-zinc-500 dark:text-zinc-400;
}

.cf-empty {
  @apply flex items-center gap-3 w-full px-3 py-3 text-left rounded-10 border-2 border-dashed border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20 hover:border-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors;
}

.cf-empty-icon {
  @apply flex items-center justify-center shrink-0 w-8 h-8 rounded-full bg-blue-500 text-white;
}

.cf-empty-text {
  @apply flex flex-col;
}

.cf-empty-title {
  @apply text-sm font-medium text-zinc-800 dark:text-zinc-200;
}

.cf-empty-description {
  @apply text-xs text-zinc-500 dark:text-zinc-400;
}

.cf-list {
  @apply flex flex-col rounded-10 ring-1 ring-slate-300 dark:ring-zinc-700 bg-white dark:bg-zinc-800 divide-y divide-slate-200 dark:divide-zinc-700;
}

.cf-row-open {
  @apply bg-zinc-50 dark:bg-zinc-800/60;
}

.cf-row:first-child {
  @apply rounded-t-10;
}

.cf-row:last-child {
  @apply rounded-b-10;
}

.cf-summary {
  @apply flex items-center gap-2 w-full px-3 py-2.5 text-left text-sm text-zinc-800 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-700/40 transition-colors;
}

.cf-chevron {
  @apply text-zinc-400 shrink-0;
}

.cf-summary-label {
  @apply flex-1 min-w-0 truncate;
}

.cf-summary-label-empty {
  @apply text-red-600 dark:text-red-400;
}

.cf-required-mark {
  @apply ml-0.5 text-red-600 dark:text-red-400;
}

.cf-warning {
  @apply text-red-600 dark:text-red-400 shrink-0;
}

.cf-type-badge {
  @apply shrink-0 text-xs px-2 py-0.5 rounded-md bg-zinc-100 text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300;
}

.cf-editor {
  @apply flex items-start gap-1 p-3;
}

.cf-editor-content {
  @apply flex flex-col gap-2 flex-1 min-w-0;
}

/* Wraps the type select under the label on narrow screens instead of squeezing the label. */
.cf-editor-main {
  @apply flex flex-wrap items-start gap-2;
}

.cf-icon-button {
  @apply flex items-center justify-center p-2 rounded-10 text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors;
}

.cf-options {
  @apply flex flex-wrap items-center gap-1.5;
}

.cf-chip {
  @apply inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-md text-xs bg-blue-200 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300;
}

.cf-chip-remove {
  @apply flex items-center rounded p-0.5 hover:bg-blue-300 dark:hover:bg-blue-800/60;
}

.cf-option-input {
  @apply h-7 w-44 px-2 text-xs rounded-md ring-1 ring-slate-300 dark:ring-zinc-600 bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-200 outline-none focus:ring-blue-500 border-0;
}

.cf-hint {
  @apply w-full text-xs text-zinc-500 dark:text-zinc-400 m-0;
}

.cf-editor-footer {
  @apply flex items-center justify-between;
}
</style>
